import { useTraduccion } from '@tallerya/i18n';
import { BrowserRouter, Route, Routes } from 'react-router';
import { Layout } from './layout/Layout';
import { ProveedorTitulo, useTituloPantalla } from './layout/titulo';
import { ListadoActivos } from './paginas/activos/ListadoActivos';
import { FichaActivo } from './paginas/activos/FichaActivo';
import { FormularioActivo } from './paginas/activos/FormularioActivo';
import { Guardia } from './permisos/Guardia';
import { Dashboard } from './paginas/dashboard/Dashboard';
import { ListadoEstadosDeOrden } from './paginas/estados-de-orden/ListadoEstadosDeOrden';
import { ListadoCatalogos } from './paginas/catalogos/ListadoCatalogos';
import { ListadoMarcas } from './paginas/marcas/ListadoMarcas';
import { FormularioOrden } from './paginas/ordenes/FormularioOrden';
import { ListadoOrdenes } from './paginas/ordenes/ListadoOrdenes';
import { FichaOrden } from './paginas/ordenes/FichaOrden';
import { FichaGasto } from './paginas/gastos/FichaGasto';
import { ListadoGastos } from './paginas/gastos/ListadoGastos';
import { FormularioSucursal } from './paginas/sucursales/FormularioSucursal';
import { ListadoSucursales } from './paginas/sucursales/ListadoSucursales';
import { FichaProveedor } from './paginas/proveedores/FichaProveedor';
import { FormularioProveedor } from './paginas/proveedores/FormularioProveedor';
import { ListadoProveedores } from './paginas/proveedores/ListadoProveedores';
import { FichaItem } from './paginas/productos-servicios/FichaItem';
import { FormularioItem } from './paginas/productos-servicios/FormularioItem';
import { ListadoItems } from './paginas/productos-servicios/ListadoItems';
import { ListadoClientes } from './paginas/clientes/ListadoClientes';
import { FichaPersona } from './paginas/personal/FichaPersona';
import { FormularioPersona } from './paginas/personal/FormularioPersona';
import { ListadoPersonal } from './paginas/personal/ListadoPersonal';
import { FichaCliente } from './paginas/clientes/FichaCliente';
import { FormularioCliente } from './paginas/clientes/FormularioCliente';

// El sitio: el layout (menú, app bar y panel) y dónde se dibuja cada ruta.
//
// Se entra con Keycloak (F-5) y cada ruta con datos va dentro de una `Guardia`
// (F-6) con el permiso que pide la API.
//
// **Ocultar no es proteger**: la guarda es comodidad para quien mira la
// pantalla, y la comprobación que vale vive en el servidor.
export function App() {
  return (
    <BrowserRouter>
      <ProveedorTitulo>
        <Layout>
          <Routes>
            {/* Inicio no pide permiso, pero sí pasa por la guardia: es lo primero
                que ve el dueño de un taller suspendido, y ahí tiene que enterarse
                (#243). */}
            <Route
              path="/"
              element={
                <Guardia>
                  <Dashboard />
                </Guardia>
              }
            />
            {/* Sucursales (M-1). Las tres rutas piden `settings.edit`, el mismo
                permiso que exige la API: sin él, la pantalla lo dice en vez de
                mostrarse llena de errores 403. Asignar personal pide además
                `staff.manage`, y eso lo responde la API al guardar. */}
            <Route
              path="/sucursales"
              element={
                <Guardia permiso="settings.edit">
                  <ListadoSucursales />
                </Guardia>
              }
            />
            <Route
              path="/sucursales/nueva"
              element={
                <Guardia permiso="settings.edit">
                  <FormularioSucursal />
                </Guardia>
              }
            />
            <Route
              path="/sucursales/:id"
              element={
                <Guardia permiso="settings.edit">
                  <FormularioSucursal />
                </Guardia>
              }
            />

            {/* Proveedores (M-2, #56). `supplier.edit`, como la API. La ficha
                (#188) llegó con el historial. La ventana de crear al vuelo no
                tiene ruta: la abren los gastos (#143). */}
            <Route
              path="/proveedores"
              element={
                <Guardia permiso="supplier.edit">
                  <ListadoProveedores />
                </Guardia>
              }
            />
            <Route
              path="/proveedores/nuevo"
              element={
                <Guardia permiso="supplier.edit">
                  <FormularioProveedor />
                </Guardia>
              }
            />
            <Route
              path="/proveedores/:id"
              element={
                <Guardia permiso="supplier.edit">
                  <FichaProveedor />
                </Guardia>
              }
            />
            <Route
              path="/proveedores/:id/editar"
              element={
                <Guardia permiso="supplier.edit">
                  <FormularioProveedor />
                </Guardia>
              }
            />

            {/* Mecánicos/Operadores (M-4, #58). `staff.manage`, como la API: no
                hay un permiso de solo lectura. El acceso al sistema es de #208. */}
            <Route
              path="/personal"
              element={
                <Guardia permiso="staff.manage">
                  <ListadoPersonal />
                </Guardia>
              }
            />
            <Route
              path="/personal/nuevo"
              element={
                <Guardia permiso="staff.manage">
                  <FormularioPersona />
                </Guardia>
              }
            />
            <Route
              path="/personal/:id"
              element={
                <Guardia permiso="staff.manage">
                  <FichaPersona />
                </Guardia>
              }
            />
            <Route
              path="/personal/:id/editar"
              element={
                <Guardia permiso="staff.manage">
                  <FormularioPersona />
                </Guardia>
              }
            />

            {/* Productos/Servicios (M-3): el listado (#112) y el formulario
                (#113), que también es el de duplicar (`/nuevo?duplicar=:id`).
                `catalog.edit`, como la API. */}
            <Route
              path="/productos-servicios"
              element={
                <Guardia permiso="catalog.edit">
                  <ListadoItems />
                </Guardia>
              }
            />
            <Route
              path="/productos-servicios/nuevo"
              element={
                <Guardia permiso="catalog.edit">
                  <FormularioItem />
                </Guardia>
              }
            />
            <Route
              path="/productos-servicios/:id"
              element={
                <Guardia permiso="catalog.edit">
                  <FichaItem />
                </Guardia>
              }
            />
            <Route
              path="/productos-servicios/:id/editar"
              element={
                <Guardia permiso="catalog.edit">
                  <FormularioItem />
                </Guardia>
              }
            />

            {/* Clientes (M-5). `customer.edit` */}
            <Route
              path="/clientes"
              element={
                <Guardia permiso="customer.edit">
                  <ListadoClientes />
                </Guardia>
              }
            />
            <Route
              path="/clientes/nuevo"
              element={
                <Guardia permiso="customer.edit">
                  <FormularioCliente />
                </Guardia>
              }
            />
            {/* La ficha (#105). Sus últimas órdenes piden además `order.view`, y
                eso lo resuelve la propia pantalla: sin él, la sección no está. */}
            <Route
              path="/clientes/:id"
              element={
                <Guardia permiso="customer.edit">
                  <FichaCliente />
                </Guardia>
              }
            />
            <Route
              path="/clientes/:id/editar"
              element={
                <Guardia permiso="customer.edit">
                  <FormularioCliente />
                </Guardia>
              }
            />

            {/* Marcas y modelos (M-7). `customer.edit`, como la API: son del
                contexto de Clientes y activos, y el mecánico las usa al
                registrar un carro. */}
            <Route
              path="/marcas"
              element={
                <Guardia permiso="customer.edit">
                  <ListadoMarcas />
                </Guardia>
              }
            />

            {/* Estados de la orden (#145): es configuración del taller, no de la orden. */}
            <Route
              path="/estados-de-orden"
              element={
                <Guardia permiso="settings.edit">
                  <ListadoEstadosDeOrden />
                </Guardia>
              }
            />

            {/* Catálogos (#154): categorías, especialidades y tipos de activo,
                que antes tenían pantalla propia (#117). La guardia no pide permiso:
                cada catálogo pide el suyo —el de su API—, y la pantalla muestra solo
                los que quien entra puede mantener. Está para el taller suspendido
                (#243), que si no se leería como «tu rol no puede». */}
            <Route
              path="/catalogos"
              element={
                <Guardia>
                  <ListadoCatalogos />
                </Guardia>
              }
            />

            {/* Activos (#119). `customer.edit`, como la API: son del contexto de
                Clientes, y el mecánico los registra al leer la placa. La ficha
                (#187) llegó con el historial; la edición lleva la galería de
                adjuntos (B-26), que antes vivía en una página provisional.
                `/activos/nuevo?cliente=<id>` llega desde la ficha del cliente
                (#105). */}
            <Route
              path="/activos"
              element={
                <Guardia permiso="customer.edit">
                  <ListadoActivos />
                </Guardia>
              }
            />
            <Route
              path="/activos/nuevo"
              element={
                <Guardia permiso="customer.edit">
                  <FormularioActivo />
                </Guardia>
              }
            />
            <Route
              path="/activos/:id"
              element={
                <Guardia permiso="customer.edit">
                  <FichaActivo />
                </Guardia>
              }
            />
            <Route
              path="/activos/:id/editar"
              element={
                <Guardia permiso="customer.edit">
                  <FormularioActivo />
                </Guardia>
              }
            />

            {/* Órdenes de servicio. El listado (#139) y la ficha (#131) piden
                `order.view` y el alta `order.edit`, como la API; editar la
                cabecera desde la ficha pide `order.edit`, y eso lo resuelve la
                pantalla, igual que el botón «Nueva orden» del listado. */}
            <Route
              path="/ordenes"
              element={
                <Guardia permiso="order.view">
                  <ListadoOrdenes />
                </Guardia>
              }
            />
            <Route
              path="/ordenes/nueva"
              element={
                <Guardia permiso="order.edit">
                  <FormularioOrden />
                </Guardia>
              }
            />
            <Route
              path="/ordenes/:id"
              element={
                <Guardia permiso="order.view">
                  <FichaOrden />
                </Guardia>
              }
            />
            {/* El listado de la caja (#144). `expense.edit` y no `expense.create`, que
                es lo que pide la API para leer: la pantalla es de quien paga (#170). */}
            <Route
              path="/gastos"
              element={
                <Guardia permiso="expense.edit">
                  <ListadoGastos />
                </Guardia>
              }
            />
            {/* La ficha del gasto (#189), con el historial. `expense.create`, como la
                API: se llega también desde la orden, donde lo ve quien registra. */}
            <Route
              path="/gastos/:id"
              element={
                <Guardia permiso="expense.create">
                  <FichaGasto />
                </Guardia>
              }
            />

            {/* Cualquier otra dirección. Sin esto, escribir mal una ruta deja la
                pantalla en blanco sin decir por qué. */}
            <Route path="*" element={<NoEncontrada />} />
          </Routes>
        </Layout>
      </ProveedorTitulo>
    </BrowserRouter>
  );
}

function NoEncontrada() {
  const { t } = useTraduccion();
  useTituloPantalla(t('admin.noEncontrada.titulo'));
  return (
    <>
      <h1>{t('admin.noEncontrada.titulo')}</h1>
      <p>{t('admin.noEncontrada.texto')}</p>
    </>
  );
}
